import { useContext, useEffect, useRef, useState } from "react";
import { IComment, IPost } from "../models";
import {
  CommentsV2Service,
  PostsV2Service,
  ShapeFilesV1Service,
} from "../../../common/services/api/api";
import { Loading } from "../../../common/components/loading/loading";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FaEllipsisH, FaGlobeAsia } from "react-icons/fa";
import { PostCardMenu } from "../postList/postCard/postCard";
import Context from "../../../common/components/utils/context";
import { FaComment, FaHeart } from "react-icons/fa6";
import { IoSend } from "react-icons/io5";
import Moment from "react-moment";
import { IAccount } from "../../../common/models/accounts";

const PostView = ({ postId, modal, isSearch, sendCommentCount }: IPostView) => {
  const context = useContext(Context);
  const service = new PostsV2Service();
  const shapeFileService = new ShapeFilesV1Service();
  const { id } = useParams<{ id: string }>();
  const message = useRef<HTMLTextAreaElement>(null);
  const current = (postId ?? id) as string;
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState<IPost>({} as IPost);
  const [liked, setLiked] = useState(false);
  const date = new Date(post.created);

  const handleLikeClick = async () => {
    const response = liked
      ? await service.unlike(current)
      : await service.like(current);

    if (response.status === 200) {
      setLiked(!liked);
      fetchPost(false);
    }
  };

  const handleAddComment = async () => {
    if (message.current?.value.length) {
      try {
        await service
          .comment(current, message.current.value)
          .then((response) => {
            fetchPost(false);
            const newCommentCount =
              post.comments.length > 0 ? post.comments.length + 1 : 1;
            if (sendCommentCount) {
              sendCommentCount(newCommentCount);
            }
          });
      } catch (error) {
        console.error("Error commenting on post:", error);
      }
    }
  };

  const fetchPost = async (reload: boolean) => {
    try {
      setLoading(reload);
      if (current) {
        // await service.get(current).then((response) => setPost(response.data));
        const postResponse = await service.get(current);
        const shapeFileId = postResponse.data.shapeFileId;
        // console.warn("MASSA - fetchPost() ", response.data);
        if (shapeFileId) {
          const shapeFileResponse = await shapeFileService.get(shapeFileId);
          console.warn("MASSA - shapeFileResponse ", shapeFileResponse.data);
        }
        setPost(postResponse.data);
        // });
        await service
          .isLiked(current)
          .then((response) => setLiked(response.data));
      }
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost(true);
  }, [id, postId, context.modal.show]);

  const PostViewContainer = () => {
    return (
      <main className="container mx-auto px-4 py-8 mb-auto relative">
        <div className="grid grid-cols-1 gap-6 justify-items-center">
          <PostViewPartial />
        </div>
      </main>
    );
  };

  const PostViewPartial = () => {
    return (
      <main className="container mx-auto px-4 py-8 mb-auto relative">
        {modal ? null : (
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg text-gray-800 text-sm tracking-wider font-medium outline-none bg-text-link px-5 py-2 cursor-pointer"
          >
            Back
          </button>
        )}
        <article
          className={`flex flex-col md:flex-row w-full ${
            modal ? null : "sm:w-2/3"
          } mx-auto bg-white rounded-xl overflow-hidden shadow-md`}
        >
          <img
            className={`w-full ${
              isSearch ? "md:w-1/2" : "md:w-2/3"
            } object-contain`}
            src={post.pictureURL}
          />
          <div className="w-full md:w-1/3 flex flex-col space-y-4 p-4">
            <div className="flex">
              <div className="flex items-center w-5/6">
                <div className="pr-3">
                  <img
                    className="h-8 w-8 object-contain border rounded-full"
                    src={post.creatorAvatarURL}
                    alt={post.userName}
                  />
                </div>
                <div className="block">
                  <Link
                    to={`/accounts/${post.accountId}`}
                    className="tracking-wide block text-sm text-gray-800"
                  >
                    {post.userName}
                  </Link>
                  <time
                    dateTime={date.toString()}
                    className="block text-xs text-gray-800"
                  >
                    <Moment date={post.created} format="MMMM D [at] hh:mm A" />
                  </time>
                </div>
              </div>
              <div className="flex items-center w-1/6">
                <div className="w-full relative">
                  <span
                    className="flex items-center float-right space-x-1 hover:text-blue-600 text-gray-500 cursor-pointer"
                    aria-expanded={dropdown}
                    aria-haspopup="true"
                    onClick={() => setDropdown(!dropdown)}
                  >
                    <FaEllipsisH />
                    {dropdown ? (
                      <PostCardMenu
                        owned={context.account.accountId === post.accountId}
                        postId={post.id}
                        dropdown={setDropdown}
                      />
                    ) : null}
                  </span>
                </div>
              </div>
            </div>
            <p
              hidden={!post.body}
              className="font-normal text-sm text-gray-700 whitespace-pre-line"
            >
              {post.body}
            </p>
            {JSON.parse(post.params)?.actions[0]?.value && (
              <p>
                <a
                  href={JSON.parse(post.params)?.actions[0]?.value}
                  target="_blank"
                  className="font-normal text-sm text-gray-400 hover:text-gray-700 flex items-center space-x-1"
                >
                  <FaGlobeAsia />
                  <span>{JSON.parse(post.params)?.actions[0]?.value}</span>
                </a>
              </p>
            )}
            {post.what3words && (
              <p>
                <a
                  href={"https://what3words.com/" + post.what3words}
                  target="_blank"
                  className="font-normal text-sm text-gray-400 hover:text-gray-700 flex items-center space-x-1"
                >
                  <FaGlobeAsia />
                  <span>{"https://what3words.com/" + post.what3words}</span>
                </a>
              </p>
            )}
            <div className="flex">
              <div className="w-1/2">
                <div
                  onClick={handleLikeClick}
                  className={`flex items-center space-x-1 cursor-pointer ${
                    liked ? "text-blue-500" : "text-gray-400"
                  }`}
                >
                  <FaHeart size={14} />
                  <small>{post.likes}</small>
                </div>
              </div>
              <div className="w-1/2">
                <div className="flex items-center space-x-1 justify-end text-gray-500">
                  <FaComment size={14} />
                  <small>{post.comments.length}</small>
                </div>
              </div>
            </div>
            <hr />
            <label
              htmlFor={`comment-${post.id}`}
              className="flex flex-col bg-gray-100 p-2 rounded"
            >
              <textarea
                ref={message}
                id={`comment-${post.id}`}
                placeholder="Write a comment"
                className="bg-transparent text-sm resize-none focus:outline-none placeholder:text-gray-300 text-gray-700"
              />
              <button
                onClick={handleAddComment}
                className="flex items-center space-x-1 justify-end text-gray-400"
              >
                <IoSend size={16} />
              </button>
            </label>
            {post.comments.length > 0 ? (
              <PostViewComments
                comments={post.comments}
                account={context.account}
                post={post.id}
              />
            ) : null}
          </div>
        </article>
      </main>
    );
  };

  return loading ? (
    <Loading />
  ) : modal ? (
    <PostViewPartial />
  ) : (
    <PostViewContainer />
  );
};

const PostViewComments = ({ comments, account, post }: IPostViewComments) => {
  return (
    <div className="flex flex-col space-y-5 lg:max-h-96 lg:overflow-y-auto">
      <h3 className="text-gray-800 sans font-medium">Comments</h3>
      {comments.map((comment) => (
        <PostViewComment
          key={comment.id}
          comment={comment}
          account={account.accountId}
          post={post}
        />
      ))}
    </div>
  );
};

const PostViewComment = ({ comment, account, post }: IPostViewComment) => {
  const [liked, setLiked] = useState(
    comment.userLikes?.indexOf(account) !== -1
  );

  const handleLikeClick = async () => {
    const service = new CommentsV2Service();
    const response = liked
      ? await service.unlike(post, comment.id)
      : await service.like(post, comment.id);

    if (response.status === 200) {
      setLiked(!liked);
    }
  };

  return (
    <div className="flex flex-col space-y-3">
      <Link
        to={`/accounts/${comment.accountId}`}
        className="flex items-center tracking-wide block text-sm text-gray-800"
      >
        <img
          className="h-8 w-8 mr-2 object-contain border rounded-full"
          src={comment.accountPictureUrl}
          alt={comment.username}
        />
        {comment.username}
      </Link>
      <p className="text-gray-800">{comment.message}</p>
      <div className="flex">
        <div className="w-4/5 flex space-x-3">
          <div className="text-gray-400">
            <small>
              <Moment fromNow ago date={comment.created} /> ago
            </small>
          </div>
          <div
            className={
              liked
                ? "text-blue-500 hover:text-blue-700"
                : "text-gray-400 hover:text-gray-800"
            }
          >
            <small onClick={handleLikeClick} className="cursor-pointer">
              {liked ? "Liked" : "Like"}
            </small>
          </div>
        </div>
        <div className="w-1/5">
          <div className="flex items-center space-x-1 justify-end text-gray-500">
            <FaComment size={14} />
            <small>0</small>
          </div>
        </div>
      </div>
      <hr />
    </div>
  );
};

interface IPostView {
  postId?: string;
  modal?: boolean;
  isSearch?: boolean;
  sendCommentCount?: (count: number) => void;
}

export interface IPostViewComments {
  comments: IComment[];
  account: IAccount;
  post: string;
}

export interface IPostViewComment {
  comment: IComment;
  account: string;
  post: string;
}

export default PostView;
